import type { LiftSummary, StrengthSetInput } from '~/calculations/estimated1RM'
import { summariseLift } from '~/calculations/estimated1RM'
import type { SentinelLiftRecord } from '~/models/sentinelLift'
import type { StrengthEntryInput, StrengthEntryRecord } from '~/models/strengthEntry'
import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import {
  createSentinelLift,
  listActiveSentinelLifts,
  setSentinelLiftStatus,
} from '~/storage/repositories/sentinelLiftRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import {
  addStrengthEntry,
  listAllStrengthEntriesForLift,
} from '~/storage/repositories/strengthEntryRepository'
import type { SentinelLiftId } from '~/types/ids'
import { ok, type Result } from '~/utils/result'
import { todayFor } from './weightService'

/**
 * Sentinel lifts and their estimated 1RM summaries (Checkpoint 2.2).
 *
 * The impure boundary: it reads the lifts, their sets and the configured week
 * start, then hands plain values to the pure `summariseLift`. Nothing about the
 * estimate is stored — it is derived on read from the raw sets, so a later
 * `_v2` formula reinterprets the same history rather than being retrofitted
 * onto numbers frozen at write time (data-model.md).
 */

export interface LiftWithSummary {
  readonly lift: SentinelLiftRecord
  readonly summary: LiftSummary
  /** This week's sets, newest first, so the step can list what was entered. */
  readonly setsThisWeek: readonly StrengthEntryRecord[]
}

function toInput(record: StrengthEntryRecord): StrengthSetInput {
  return {
    id: record.id as string,
    liftId: record.liftId as string,
    date: record.date,
    loadKg: record.loadKg as number,
    reps: record.reps,
    ...(record.bodyWeightKg === undefined ? {} : { bodyWeightKg: record.bodyWeightKg as number }),
  }
}

/**
 * Every active lift with its summary.
 *
 * Retired lifts are absent: they are no longer offered, and their history is
 * kept rather than deleted (see `sentinelLiftRepository`).
 */
export async function getLiftSummaries(
  db: SolaDatabase,
  now: Date,
): Promise<Result<readonly LiftWithSummary[], StorageError>> {
  const reference = todayFor(now)

  const settings = await getSettings(db, now)
  if (!settings.ok) return settings

  const lifts = await listActiveSentinelLifts(db)
  if (!lifts.ok) return lifts

  const summaries: LiftWithSummary[] = []

  for (const lift of lifts.value) {
    const entries = await listAllStrengthEntriesForLift(db, lift.id)
    if (!entries.ok) return entries

    const summary = summariseLift(
      lift.id as string,
      entries.value.map(toInput),
      reference,
      settings.value.weekStartDay,
      DEFAULT_RULES_THRESHOLDS.strength,
    )

    const week = summary.current.weekStartDate
    const setsThisWeek = entries.value
      .filter((entry) => entry.date >= week)
      .sort((left, right) => right.date.localeCompare(left.date))

    summaries.push({ lift, summary, setsThisWeek })
  }

  return ok(summaries)
}

/** Define a lift. Nothing seeds a default list — the user names their own. */
export async function defineLift(
  db: SolaDatabase,
  name: string,
  now: Date,
): Promise<Result<SentinelLiftRecord, StorageError>> {
  const existing = await listActiveSentinelLifts(db)
  if (!existing.ok) return existing

  const nextOrder = existing.value.reduce((highest, lift) => Math.max(highest, lift.order + 1), 0)
  return createSentinelLift(db, { name, order: nextOrder }, now)
}

/** Stop offering a lift, keeping every set recorded against it. */
export async function retireLift(
  db: SolaDatabase,
  id: SentinelLiftId,
  now: Date,
): Promise<Result<SentinelLiftRecord, StorageError>> {
  return setSentinelLiftStatus(db, id, 'retired', now)
}

/** Record one set. Sets above the rep cap are accepted and excluded later. */
export async function recordSet(
  db: SolaDatabase,
  input: Omit<StrengthEntryInput, 'date'> & { readonly date?: string },
  now: Date,
): Promise<Result<StrengthEntryRecord, StorageError>> {
  return addStrengthEntry(db, { ...input, date: input.date ?? (todayFor(now) as string) }, now)
}
